import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../store/useStore';
import useStore from '../store/useStore';

const STATUS_BADGE = {
  draft: { label: 'Draft', color: '#8a8886', bg: 'rgba(138,136,134,.12)' },
  pending: { label: 'Pending Review', color: '#d29922', bg: 'rgba(240,165,0,.1)' },
  published: { label: 'Published', color: '#107c10', bg: 'rgba(16,124,16,.1)' },
  rejected: { label: 'Rejected', color: '#d13438', bg: 'rgba(209,52,56,.1)' },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.draft;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
      color: s.color, background: s.bg, border: `1px solid ${s.color}40`,
    }}>
      {s.label}
    </span>
  );
}

export default function TemplateStudio() {
  const { user } = useStore();

  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template id being edited, or null for new
  const [form, setForm] = useState({
    name: '',
    description: '',
    html_source: '',
    primary_color: '#0078d4',
    cta_text: 'Connect Account',
  });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState('');
  const [autoGenerated, setAutoGenerated] = useState(null); // {html, primary_color, brand}
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState('success');
  const toastTimer = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast(msg);
    setToastType(type);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 4000);
  };

  const loadTemplates = useCallback(async () => {
    try {
      const r = await api.get('/api/templates');
      setTemplates(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadTemplates();
    return () => clearTimeout(toastTimer.current);
  }, [loadTemplates]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const autoDetectColor = () => {
    const html = form.html_source;
    const matches = html.match(/#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g) || [];
    const interesting = matches.filter(c => {
      const h = c.slice(1).length === 3 ? c.slice(1).split('').map(x => x + x).join('') : c.slice(1);
      const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
      const sat = Math.max(r, g, b) - Math.min(r, g, b);
      const light = (Math.max(r, g, b) + Math.min(r, g, b)) / 2;
      return sat > 40 && light > 20 && light < 235;
    });
    if (interesting.length > 0) {
      const freq = {};
      interesting.forEach(c => { freq[c] = (freq[c] || 0) + 1; });
      const top = Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0];
      set('primary_color', top);
    }
  };

  const refreshPreview = () => {
    setPreview(form.html_source);
  };

  const fullPreview = async () => {
    try {
      const resp = await api.post('/api/templates/preview-raw', form, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/html' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      showToast(e.response?.data?.detail || 'Preview failed', 'error');
    }
  };

  const autoGenerate = async () => {
    if (!form.html_source.trim()) {
      showToast('Paste your HTML source first', 'error');
      return;
    }
    setAutoGenerating(true);
    try {
      const r = await api.post('/api/templates/auto-generate', {
        html_source: form.html_source,
        cta_text: form.cta_text || 'Connect Account',
      });
      setAutoGenerated({
        html: r.data.generated_html,
        primary_color: r.data.detected_primary_color,
        brand: r.data.detected_brand,
      });
      showToast(`Auto-generated from "${r.data.detected_brand || 'pasted template'}" — review below`);
    } catch (e) {
      showToast(e.response?.data?.detail || 'Auto-generate failed', 'error');
    }
    setAutoGenerating(false);
  };

  const useAutoGenerated = () => {
    if (!autoGenerated) return;
    set('html_source', autoGenerated.html);
    if (autoGenerated.primary_color) set('primary_color', autoGenerated.primary_color);
    if (autoGenerated.brand && !form.name) set('name', autoGenerated.brand + ' — ClassMail');
    setAutoGenerated(null);
    setPreview(autoGenerated.html);
    showToast('Auto-generated template loaded into editor');
  };

  const save = async (submit = false) => {
    if (!form.html_source.trim()) {
      showToast('HTML source is required', 'error');
      return;
    }
    if (!form.name.trim()) {
      showToast('Template name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      let result;
      if (editing) {
        result = await api.put(`/api/templates/${editing}`, form);
      } else {
        result = await api.post('/api/templates', form);
      }
      if (submit) {
        await api.post(`/api/templates/${result.data.id}/submit`);
      }
      setEditing(result.data.id);
      loadTemplates();
      showToast(submit ? 'Submitted for review' : 'Saved as draft');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Save failed', 'error');
    }
    setSaving(false);
  };

  const startEdit = (t) => {
    setEditing(t.id);
    setForm({
      name: t.name || '',
      description: t.description || '',
      html_source: t.html_source || '',
      primary_color: t.primary_color || '#0078d4',
      cta_text: t.cta_text || 'Connect Account',
    });
    setPreview('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startNew = () => {
    setEditing(null);
    setForm({
      name: '',
      description: '',
      html_source: '',
      primary_color: '#0078d4',
      cta_text: 'Connect Account',
    });
    setPreview('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/api/templates/${id}`);
      if (editing === id) {
        startNew();
      }
      loadTemplates();
      showToast('Template deleted');
    } catch {
      showToast('Delete failed', 'error');
    }
  };

  const submitTemplate = async (id) => {
    try {
      await api.post(`/api/templates/${id}/submit`);
      loadTemplates();
      showToast('Submitted for review');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Submit failed', 'error');
    }
  };

  const previewSaved = async (id) => {
    try {
      const resp = await api.get(`/api/templates/${id}/preview`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/html' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      showToast('Preview failed', 'error');
    }
  };

  const ownTemplates = templates.filter(t => t.owner_id === user?.id || user?.role === 'superadmin');
  const publishedTemplates = templates.filter(t => t.status === 'published' && t.owner_id !== user?.id);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 0 60px' }}>
      <div className="page-header" style={{ marginBottom: 24 }}>
        <h1>Template Studio</h1>
        <p>Paste any HTML template and inject the ClassMail device-code overlay. Submit for superadmin review to make it available to all admins.</p>
      </div>

      {/* ── Editor + Preview split layout ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '60% 40%', gap: 16, marginBottom: 24 }}>

        {/* Left: Editor */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 0, padding: 0, overflow: 'hidden' }}>
          {/* Editor header */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', flex: 1 }}>
              {editing ? 'Editing Template' : 'New Template'}
            </span>
            {editing && (
              <button className="btn btn-ghost btn-sm" onClick={startNew}>+ New</button>
            )}
          </div>

          <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Name */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Template Name</label>
              <input
                className="form-input"
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g. Dark Landing — Microsoft"
              />
            </div>

            {/* Description */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Description <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
              <input
                className="form-input"
                value={form.description}
                onChange={e => set('description', e.target.value)}
                placeholder="Short description of this template"
              />
            </div>

            {/* HTML Editor */}
            <div className="form-group" style={{ margin: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                <label className="form-label" style={{ margin: 0 }}>HTML Source</label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={autoGenerate}
                    disabled={autoGenerating}
                    title="Extract the brand style from your pasted HTML and generate a full ClassMail page in that style"
                    style={{ whiteSpace: 'nowrap', fontSize: 12 }}
                  >
                    {autoGenerating ? <>&#9696; Generating…</> : '✨ Auto Generate'}
                  </button>
                  <span style={{
                    fontSize: 11, color: 'var(--text3)',
                    background: 'var(--surface2)', border: '1px solid var(--border)',
                    borderRadius: 4, padding: '2px 8px', cursor: 'default',
                  }} title="Place this placeholder where you want the CTA button to appear. If omitted, a fixed bottom bar is injected.">
                    Tip: use {'{{CLASSMAIL_CTA}}'} placeholder
                  </span>
                </div>
              </div>
              <textarea
                value={form.html_source}
                onChange={e => set('html_source', e.target.value)}
                placeholder={`Paste your HTML template here...\n\nUse {{CLASSMAIL_CTA}} where you want the Connect button to appear.\nIf omitted, a fixed bottom bar with the button will be injected automatically.`}
                style={{
                  width: '100%',
                  height: 360,
                  background: '#0d1117',
                  color: '#c9d1d9',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: '12px 14px',
                  fontFamily: "'Consolas', 'Cascadia Code', monospace",
                  fontSize: 13,
                  lineHeight: 1.6,
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                spellCheck={false}
              />
              <div className="form-hint">
                Supports Bootstrap, Tailwind, custom CSS, or any HTML. The overlay is injected automatically and does not depend on your template's CSS.
              </div>
            </div>

            {/* Color + CTA */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ margin: 0, flex: '1 1 180px' }}>
                <label className="form-label">Primary Color</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={form.primary_color}
                    onChange={e => set('primary_color', e.target.value)}
                    style={{ width: 36, height: 34, padding: 2, border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', background: 'var(--surface2)' }}
                  />
                  <input
                    className="form-input"
                    value={form.primary_color}
                    onChange={e => set('primary_color', e.target.value)}
                    placeholder="#0078d4"
                    style={{ flex: 1, fontFamily: 'monospace', fontSize: 13 }}
                  />
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={autoDetectColor}
                    title="Detect the most prominent non-neutral color from your HTML"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Auto-detect
                  </button>
                </div>
                <div className="form-hint">Used for the overlay modal and CTA button.</div>
              </div>

              <div className="form-group" style={{ margin: 0, flex: '1 1 180px' }}>
                <label className="form-label">CTA Button Text</label>
                <input
                  className="form-input"
                  value={form.cta_text}
                  onChange={e => set('cta_text', e.target.value)}
                  placeholder="Connect Account"
                />
              </div>
            </div>

            {/* Auto-generated preview */}
            {autoGenerated && (
              <div style={{
                border: '2px solid #0078d4', borderRadius: 8, overflow: 'hidden',
                background: 'rgba(0,120,212,.04)',
              }}>
                <div style={{
                  padding: '10px 14px', background: 'rgba(0,120,212,.08)',
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', flex: 1 }}>
                    ✨ Auto-Generated Template
                    {autoGenerated.brand && (
                      <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 6 }}>
                        from "{autoGenerated.brand}"
                      </span>
                    )}
                  </span>
                  {autoGenerated.primary_color && (
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%',
                      background: autoGenerated.primary_color, display: 'inline-block',
                      border: '2px solid rgba(0,0,0,.15)', flexShrink: 0,
                    }} title={`Detected: ${autoGenerated.primary_color}`} />
                  )}
                  <button className="btn btn-primary btn-sm" onClick={useAutoGenerated}>
                    Use this Template
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    const url = URL.createObjectURL(new Blob([autoGenerated.html], { type: 'text/html' }));
                    window.open(url, '_blank');
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                  }}>
                    Open ↗
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAutoGenerated(null)}
                    style={{ color: 'var(--text3)' }}>
                    ✕
                  </button>
                </div>
                <iframe
                  srcDoc={autoGenerated.html}
                  sandbox="allow-scripts"
                  style={{ width: '100%', height: 380, border: 'none', display: 'block' }}
                  title="auto-generated-preview"
                />
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 4 }}>
              <button className="btn btn-primary" onClick={() => save(false)} disabled={saving}>
                {saving ? <><span className="spinner" style={{ width: 13, height: 13, marginRight: 6 }} />Saving…</> : 'Save as Draft'}
              </button>
              <button className="btn btn-success" onClick={() => save(true)} disabled={saving}>
                {saving ? 'Saving…' : 'Save & Submit for Review'}
              </button>
            </div>
          </div>
        </div>

        {/* Right: Preview + settings */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Live Preview</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" onClick={refreshPreview}>
                Refresh Preview
              </button>
              <button className="btn btn-ghost btn-sm" onClick={fullPreview}>
                Full Preview (with overlay) ↗
              </button>
            </div>
            {preview ? (
              <iframe
                srcDoc={preview}
                sandbox="allow-scripts"
                style={{ width: '100%', height: 400, border: '1px solid var(--border)', borderRadius: 6 }}
                title="template-preview"
              />
            ) : (
              <div style={{
                height: 200, border: '1px dashed var(--border)', borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', gap: 8, color: 'var(--text3)', fontSize: 13,
              }}>
                <span style={{ fontSize: 28 }}>🎨</span>
                <span>Click "Refresh Preview" to see your template</span>
                <span style={{ fontSize: 11 }}>or "Full Preview" to see it with the ClassMail overlay</span>
              </div>
            )}
          </div>

          {/* Color preview swatch */}
          {form.primary_color && (
            <div className="card" style={{ padding: '12px 16px' }}>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>Color Preview</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 6, background: form.primary_color, flexShrink: 0 }} />
                <div>
                  <div style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>
                    {form.primary_color}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                    Used for overlay header, CTA button, step badges
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Template List ── */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">My Templates</span>
          <button className="btn btn-primary btn-sm" onClick={startNew}>+ New Template</button>
        </div>

        {ownTemplates.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🎨</div>
            <p>No templates yet. Paste your HTML above to create one.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {ownTemplates.map(t => (
              <div key={t.id} style={{
                border: `1px solid ${editing === t.id ? 'var(--primary, #0078d4)' : 'var(--border)'}`,
                borderRadius: 'var(--radius)',
                padding: '14px 16px',
                background: editing === t.id ? 'rgba(0,120,212,.04)' : 'var(--surface2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text)', flex: 1 }}>{t.name}</span>
                  <StatusBadge status={t.status} />
                  {t.reject_reason && (
                    <span
                      title={`Rejection reason: ${t.reject_reason}`}
                      style={{ fontSize: 11, color: '#a4262c', cursor: 'help', textDecoration: 'underline dotted' }}
                    >
                      View reason
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                    {new Date(t.created_at).toLocaleDateString()}
                  </span>
                </div>
                {t.description && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>{t.description}</div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => startEdit(t)}>
                    Edit
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => previewSaved(t.id)}>
                    Preview ↗
                  </button>
                  {(t.status === 'draft' || t.status === 'rejected') && (
                    <button className="btn btn-ghost btn-sm" onClick={() => submitTemplate(t.id)}>
                      Submit for Review
                    </button>
                  )}
                  <button className="btn btn-danger btn-sm" onClick={() => deleteTemplate(t.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Published templates from others */}
      {publishedTemplates.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">
            <span className="card-title">Published Templates</span>
            <span className="badge badge-success" style={{ fontSize: 11 }}>Available to use</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {publishedTemplates.map(t => (
              <div key={t.id} style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                padding: '14px 16px',
                background: 'var(--surface2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text)', flex: 1 }}>{t.name}</span>
                  <StatusBadge status={t.status} />
                  {t.owner_username && (
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>by {t.owner_username}</span>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                    {t.published_at ? `Published ${new Date(t.published_at).toLocaleDateString()}` : new Date(t.created_at).toLocaleDateString()}
                  </span>
                </div>
                {t.description && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>{t.description}</div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => previewSaved(t.id)}>
                    Preview ↗
                  </button>
                  <span style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center' }}>
                    Use by selecting this template when generating a link
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: toastType === 'success' ? '#238636' : '#da3633',
          color: '#fff', padding: '12px 20px', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,.5)', fontWeight: 600, fontSize: 14,
          minWidth: 200, textAlign: 'center',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
